/**
 * Retailer Card Component
 * 
 * Visual card for overview dashboard showing retailer summary.
 * Clicking card drills down to retailer-specific detail dashboard.
 */

import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, TrendingUp, TrendingDown, Package, Store, ShoppingCart, AlertCircle } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { getRetailerInfo } from '../config/retailers';

interface RetailerCardProps {
  retailerId: string;
  revenue: number | null;
  units: number | null;
  skuCount: number | null;
  storeCount: number | null;
  hasData: boolean;
  className?: string;
  // LW/LY comparison data
  revenueLwPct?: number | null;
  revenueLyPct?: number | null;
  // Fallback data indicator (e.g. "Jan 12 – Jan 18" when showing older week)
  dataAsOf?: string | null;
}

export function RetailerCard({
  retailerId,
  revenue,
  units,
  skuCount,
  storeCount,
  hasData,
  className = '',
  revenueLwPct,
  revenueLyPct,
  dataAsOf
}: RetailerCardProps) {
  const { setSelectedRetailerId } = useTenant();
  const retailerInfo = getRetailerInfo(retailerId);

  if (!retailerInfo) return null;

  const { name, slug, colors, logoPath } = retailerInfo;

  const formatCurrency = (value: number | null) => {
    if (value === null) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number | null) => {
    if (value === null) return 'N/A';
    return new Intl.NumberFormat('en-US').format(value);
  };

  const handleClick = () => {
    if (!hasData) return; // Don't navigate if no data
    setSelectedRetailerId(retailerId);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
      whileHover={hasData ? { scale: 1.02, transition: { duration: 0.2 } } : {}}
      onClick={handleClick}
      style={{
        borderColor: hasData ? colors.primary : '#E5E7EB',
        borderWidth: '2px',
      }}
      className={`
        group relative overflow-hidden
        bg-white rounded-xl 
        p-4 transition-shadow duration-300
        ${hasData 
          ? 'cursor-pointer hover:shadow-xl' 
          : 'cursor-not-allowed opacity-60'
        }
        ${className}
      `}
    >
      {/* Retailer Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          {logoPath ? (
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center p-1.5 border-2"
              style={{ 
                borderColor: colors.medium,
                backgroundColor: colors.light 
              }}
            >
              <img 
                src={logoPath} 
                alt={`${name} logo`}
                className="w-full h-full object-contain"
                onError={(e) => {
                  // Fallback to icon if logo fails to load
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
              <Store 
                className="hidden w-6 h-6"
                style={{ color: colors.primary }}
              />
            </div>
          ) : (
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: colors.light }}
            >
              <Store className="w-6 h-6" style={{ color: colors.primary }} />
            </div>
          )}
          <div>
            <h3 className="text-base font-bold text-gray-900">
              {name}
            </h3>
            {!hasData && (
              <p className="text-xs text-gray-500 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                No data available
              </p>
            )}
            {hasData && dataAsOf && (
              <p className="text-[11px] text-gray-400 mt-0.5">
                Latest: {dataAsOf}
              </p>
            )}
          </div>
        </div>
        
        {/* Arrow icon (appears on hover if has data) */}
        {hasData && (
          <ArrowRight 
            className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-all"
            style={{ color: colors.primary }}
          />
        )}
      </div>

      {/* Revenue - Main Metric */}
      <div className="mb-3">
        <div className="text-2xl font-bold text-gray-900 mb-0.5">
          {formatCurrency(revenue)}
        </div>
        <p className="text-xs text-gray-600">Total Revenue</p>
        
        {/* LW and LY Comparison Badges */}
        {hasData && (revenueLwPct !== null || revenueLyPct !== null) && (
          <div className="flex items-center gap-2 mt-1.5">
            {revenueLwPct !== null && revenueLwPct !== undefined && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                revenueLwPct >= 0 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {revenueLwPct >= 0 ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
                {revenueLwPct >= 0 ? '+' : ''}{(revenueLwPct * 100).toFixed(1)}% LW
              </span>
            )}
            {revenueLyPct !== null && revenueLyPct !== undefined && (
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                revenueLyPct >= 0 
                  ? 'bg-green-50 text-green-700 border border-green-200' 
                  : 'bg-red-50 text-red-700 border border-red-200'
              }`}>
                {revenueLyPct >= 0 ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> : <TrendingDown className="w-2.5 h-2.5 mr-0.5" />}
                {revenueLyPct >= 0 ? '+' : ''}{(revenueLyPct * 100).toFixed(1)}% LY
              </span>
            )}
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-gray-200">
        <div>
          <div className="flex items-center space-x-1 mb-0.5">
            <ShoppingCart className="w-3 h-3 text-gray-400" />
          </div>
          <div className="text-base font-semibold text-gray-900">
            {formatNumber(units)}
          </div>
          <div className="text-[10px] text-gray-600">Units</div>
        </div>
        <div>
          <div className="flex items-center space-x-1 mb-0.5">
            <Package className="w-3 h-3 text-gray-400" />
          </div>
          <div className="text-base font-semibold text-gray-900">
            {formatNumber(skuCount)}
          </div>
          <div className="text-[10px] text-gray-600">SKUs</div>
        </div>
        <div>
          <div className="flex items-center space-x-1 mb-0.5">
            <Store className="w-3 h-3 text-gray-400" />
          </div>
          <div className="text-base font-semibold text-gray-900">
            {formatNumber(storeCount)}
          </div>
          <div className="text-[10px] text-gray-600">Stores</div>
        </div>
      </div>

      {/* Hover overlay with retailer color */}
      {hasData && (
        <div 
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
          style={{
            background: `linear-gradient(135deg, ${colors.primary}08 0%, ${colors.primary}03 100%)`
          }}
        />
      )}

      {/* No data overlay message */}
      {!hasData && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 rounded-xl">
          <div className="text-center p-4">
            <AlertCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-600 font-medium">No Data Available</p>
            <p className="text-xs text-gray-500 mt-1">Upload data to view insights</p>
          </div>
        </div>
      )}
    </motion.div>
  );
}

